import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useFinanceEntry,
  useUpdateFinanceEntry,
  useChangeFinanceStatus,
  useCancelFinanceEntry,
  useFinanceCategories,
  useCreateFinanceCategory,
} from '../../../platform/api/hooks';
import { asRecord, str, asList } from '../../../platform/api/utils';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import { SelectWithCreate } from '../../../components/ui/InlineCreate';
import {
  Dd,
  Dt,
  Field,
  PAYMENT_LABELS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  SkeletonDetail,
  TYPE_LABELS,
  TYPE_VARIANTS,
  dlGrid,
  editFormSchema,
  fmtCurrency,
  inputCss,
  linkBtn,
  modalOverlay,
  primaryBtn,
  secondaryBtn,
  type EditForm,
} from './finance-detail.support';
export function FinanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: entry, isLoading, isError } = useFinanceEntry(id ?? '');
  const updateEntry = useUpdateFinanceEntry();
  const changeStatus = useChangeFinanceStatus();
  const cancelEntry = useCancelFinanceEntry();
  const { data: catData } = useFinanceCategories();
  const createCategory = useCreateFinanceCategory();
  const categories = asList(catData);
  const [editing, setEditing] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const e = asRecord(entry);
  const st = str(e.status);
  const tp = str(e.type);
  const cat = e.category as Record<string, unknown> | undefined;
  const person = e.person as Record<string, unknown> | undefined;
  // tp must be computed before this filter (TDZ / "Cannot access before initialization")
  const categoriesForType = categories.filter((c) => {
    const t = str(asRecord(c).type);
    return !t || t === tp;
  });
  const form = useForm<EditForm>({
    resolver: zodResolver(editFormSchema),
    values: {
      description: str(e.description),
      amount: str(e.amount),
      dueDate: typeof e.dueDate === 'string' ? e.dueDate.substring(0, 10) : '',
      paidAt: typeof e.paidAt === 'string' ? e.paidAt.substring(0, 10) : '',
      paymentMethod: str(e.paymentMethod),
      receiptRef: str(e.receiptRef),
      categoryId: str(e.categoryId),
      personId: str(e.personId),
    },
  });
  if (isLoading) return <SkeletonDetail />;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', margin: 0 }}>
            Não foi possível carregar este lançamento.
          </p>
          <button
            type="button"
            onClick={() => {
              void navigate('/financeiro');
            }}
            style={linkBtn}
          >
            ← Voltar para financeiro
          </button>
        </Section>
      </div>
    );
  }
  function handleSave(data: EditForm) {
    if (!id) return;
    updateEntry.mutate({
      id,
      data: {
        description: data.description,
        amount: data.amount,
        dueDate: data.dueDate !== '' ? data.dueDate : undefined,
        paidAt: data.paidAt !== '' ? data.paidAt : undefined,
        paymentMethod: data.paymentMethod !== '' ? data.paymentMethod : undefined,
        receiptRef: data.receiptRef !== '' ? data.receiptRef : undefined,
        categoryId: data.categoryId !== '' ? data.categoryId : undefined,
        personId: data.personId !== '' ? data.personId : undefined,
      },
    });
    setEditing(false);
  }
  function handleChangeStatus() {
    if (!newStatus || !id) return;
    changeStatus.mutate({ id, status: newStatus });
    setStatusOpen(false);
    setNewStatus('');
  }
  function handleCancel() {
    if (!id) return;
    cancelEntry.mutate(id);
  }
  function fmtDate(v: unknown): string {
    if (typeof v === 'string' && v.length >= 10) return v.substring(0, 10);
    if (v instanceof Date) return v.toISOString().substring(0, 10);
    return '—';
  }
  const disableCancel = st === 'CANCELLED' || cancelEntry.isPending;
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/financeiro');
        }}
        style={linkBtn}
      >
        ← Voltar para financeiro
      </button>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
            {fmtCurrency(e.amount)}
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
            <Badge label={TYPE_LABELS[tp] ?? tp} variant={TYPE_VARIANTS[tp] ?? 'default'} />
            <Badge label={STATUS_LABELS[st] ?? st} variant={STATUS_VARIANTS[st] ?? 'default'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
              }}
              style={secondaryBtn}
            >
              Editar
            </button>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setStatusOpen(true);
              }}
              style={{
                ...secondaryBtn,
                background: 'var(--info-soft)',
                color: 'var(--info)',
              }}
            >
              Alterar status
            </button>
          )}
          {!editing && st !== 'CANCELLED' && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={disableCancel}
              style={{
                ...secondaryBtn,
                background: 'var(--danger-soft)',
                color: 'var(--danger)',
              }}
            >
              {cancelEntry.isPending ? 'Cancelando...' : 'Cancelar'}
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Modal: Alterar status */}
        {statusOpen && (
          <div
            style={modalOverlay}
            onClick={(ev) => {
              if (ev.target === ev.currentTarget) {
                setStatusOpen(false);
              }
            }}
          >
            <div style={{ background: '#fff', borderRadius: 14, padding: '1.5rem', minWidth: 360 }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Alterar status</h3>
              <select
                value={newStatus}
                onChange={(ev) => {
                  setNewStatus(ev.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--line)',
                  borderRadius: 10,
                  fontSize: '0.875rem',
                  marginBottom: '0.75rem',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Selecione...</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v} disabled={v === st}>
                    {l}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setStatusOpen(false);
                  }}
                  style={secondaryBtn}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleChangeStatus}
                  disabled={!newStatus || changeStatus.isPending}
                  style={primaryBtn}
                >
                  {changeStatus.isPending ? 'Salvando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}
        {editing ? (
          <Section title="Editar lançamento">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
              <Field label="Descrição" error={form.formState.errors.description?.message}>
                <input {...form.register('description')} style={inputCss} />
              </Field>
              <Field label="Valor" error={form.formState.errors.amount?.message}>
                <input {...form.register('amount')} style={inputCss} type="number" step="0.01" />
              </Field>
              <Field label="Vencimento">
                <input {...form.register('dueDate')} type="date" style={inputCss} />
              </Field>
              <Field label="Pago em">
                <input {...form.register('paidAt')} type="date" style={inputCss} />
              </Field>
              <Field label="Método de pagamento">
                <select {...form.register('paymentMethod')} style={inputCss}>
                  <option value="">Selecione...</option>
                  {Object.entries(PAYMENT_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <SelectWithCreate
                label={`Categoria (${TYPE_LABELS[tp] ?? tp})`}
                value={form.watch('categoryId') ?? ''}
                onChange={(cid) => {
                  form.setValue('categoryId', cid);
                }}
                options={categoriesForType.map((c) => ({ id: str(c.id), name: str(c.name) }))}
                createLabel="+ Nova categoria"
                createPlaceholder="Nome da categoria"
                createHint="A categoria será do mesmo tipo deste lançamento."
                creating={createCategory.isPending}
                onCreate={async (name) => {
                  const created = await createCategory.mutateAsync({ name, type: tp || 'EXPENSE' });
                  return str(asRecord(created).id);
                }}
              />
              <Field label="Comprovante (ref)">
                <input {...form.register('receiptRef')} style={inputCss} />
              </Field>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                marginTop: '1rem',
                justifyContent: 'flex-end',
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                }}
                style={secondaryBtn}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  void form.handleSubmit(handleSave)();
                }}
                disabled={updateEntry.isPending}
                style={primaryBtn}
              >
                {updateEntry.isPending ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </Section>
        ) : (
          <>
            <Section title="Dados do lançamento">
              <dl style={dlGrid}>
                <Dt>Descrição</Dt>
                <Dd>{str(e.description)}</Dd>
                <Dt>Tipo</Dt>
                <Dd>
                  <Badge label={TYPE_LABELS[tp] ?? tp} variant={TYPE_VARIANTS[tp] ?? 'default'} />
                </Dd>
                <Dt>Valor</Dt>
                <Dd
                  style={{
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: tp === 'EXPENSE' ? 'var(--danger)' : 'var(--success)',
                  }}
                >
                  {fmtCurrency(e.amount)}
                </Dd>
                <Dt>Status</Dt>
                <Dd>
                  <Badge
                    label={STATUS_LABELS[st] ?? st}
                    variant={STATUS_VARIANTS[st] ?? 'default'}
                  />
                </Dd>
                <Dt>Categoria</Dt>
                <Dd>{cat ? str(cat.name) : '—'}</Dd>
                <Dt>Vencimento</Dt>
                <Dd>{fmtDate(e.dueDate)}</Dd>
                <Dt>Pago em</Dt>
                <Dd>{fmtDate(e.paidAt)}</Dd>
                <Dt>Método</Dt>
                <Dd>{PAYMENT_LABELS[str(e.paymentMethod)] ?? str(e.paymentMethod, '—')}</Dd>
                <Dt>Comprovante</Dt>
                <Dd>{str(e.receiptRef, '—')}</Dd>
              </dl>
            </Section>
            {person && (
              <Section title="Pessoa vinculada">
                <dl style={dlGrid}>
                  <Dt>Nome</Dt>
                  <Dd>{str(person.civilName) || str(person.socialName) || '—'}</Dd>
                  {str(person.religiousName) && (
                    <>
                      <Dt>Nome religioso</Dt>
                      <Dd style={{ fontStyle: 'italic' }}>{str(person.religiousName)}</Dd>
                    </>
                  )}
                </dl>
              </Section>
            )}
            <Section title="Metadados">
              <dl style={dlGrid}>
                <Dt>Criado em</Dt>
                <Dd>{fmtDate(e.createdAt)}</Dd>
                <Dt>Atualizado em</Dt>
                <Dd>{fmtDate(e.updatedAt)}</Dd>
                <Dt>Cancelado em</Dt>
                <Dd>{fmtDate(e.cancelledAt)}</Dd>
              </dl>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
